import type { SxProps, Theme } from "@mui/material/styles"
import { alpha } from "@mui/material"
import { designTokens, fontWeight } from "@/theme"
import { selectionFocusVisibleOutline } from "@/theme/designTokens"

/**
 * Visual tokens for the item-details modal quantity stepper
 * (`MenuItemDetailModal` footer − qty +). Card overlays use `compact`
 * with an opaque elevated surface for readability on food images.
 */
export type MenuQuantityStepperDensity = "modal" | "compact"

/** Opaque floating surface for card image overlays (not the modal shell). */
const CARD_STEPPER_BG = "#FFFFFF"
const CARD_STEPPER_BORDER = "#E5E7EB"
const CARD_STEPPER_INK = "#111827"
const CARD_STEPPER_BTN_BG = "#F3F4F6"
const CARD_STEPPER_SHADOW = "0 6px 18px rgba(15, 23, 42, 0.18)"
const CARD_STEPPER_RADIUS_PX = 14

/** Outer − qty + shell. Modal keeps translucent treatment; compact is opaque. */
export function getMenuQuantityStepperShellSx(
  theme: Theme,
  density: MenuQuantityStepperDensity = "modal"
): SxProps<Theme> {
  const compact = density === "compact"
  if (compact) {
    return {
      display: "flex",
      alignItems: "center",
      gap: designTokens.spacing.sm / 8,
      border: `1px solid ${CARD_STEPPER_BORDER}`,
      borderRadius: `${CARD_STEPPER_RADIUS_PX}px`,
      px: 0.5,
      py: 0.35,
      height: 34,
      minHeight: 34,
      backgroundColor: CARD_STEPPER_BG,
      boxShadow: CARD_STEPPER_SHADOW,
      color: CARD_STEPPER_INK,
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
    }
  }
  return {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing.md / 8,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: `${designTokens.shape.borderRadius.lg}px`,
    px: 1,
    py: 0.5,
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    boxShadow: designTokens.shadows.chip,
    color: theme.palette.text.primary,
  }
}

/** Minus / plus IconButton styles. */
export function getMenuQuantityStepperButtonSx(
  theme: Theme,
  density: MenuQuantityStepperDensity = "modal"
): SxProps<Theme> {
  const compact = density === "compact"
  if (compact) {
    return {
      width: 26,
      height: 26,
      minWidth: 26,
      minHeight: 26,
      p: 0,
      borderRadius: `${designTokens.shape.borderRadius.sm}px`,
      border: `1px solid ${CARD_STEPPER_BORDER}`,
      backgroundColor: CARD_STEPPER_BTN_BG,
      color: CARD_STEPPER_INK,
      fontWeight: fontWeight.bold,
      transition: `background ${designTokens.motion.normal}, transform ${designTokens.motion.fast}`,
      "&:hover": {
        backgroundColor: "#E5E7EB",
      },
      "&:active": {
        transform: "scale(0.96)",
        backgroundColor: "#E5E7EB",
      },
      "&:focus-visible": selectionFocusVisibleOutline(theme, 1),
    }
  }
  return {
    width: { xs: 40, md: 32 },
    height: { xs: 40, md: 32 },
    p: 0,
    borderRadius: `${designTokens.shape.borderRadius.sm}px`,
    backgroundColor: alpha(theme.palette.text.primary, 0.04),
    color: theme.palette.text.primary,
    fontWeight: fontWeight.bold,
    transition: `background ${designTokens.motion.normal}, transform ${designTokens.motion.fast}`,
    "&:hover": {
      backgroundColor: alpha(theme.palette.text.primary, 0.08),
    },
    "&:active": {
      transform: "scale(0.96)",
    },
    "&:focus-visible": selectionFocusVisibleOutline(theme, 1),
  }
}

/** Center quantity typography. */
export function getMenuQuantityStepperQtySx(
  theme: Theme,
  density: MenuQuantityStepperDensity = "modal"
): SxProps<Theme> {
  const compact = density === "compact"
  return {
    minWidth: compact ? 18 : 24,
    maxWidth: compact ? 28 : undefined,
    px: compact ? 0.15 : 0,
    textAlign: "center",
    fontWeight: compact ? 600 : fontWeight.medium,
    fontSize: compact
      ? theme.typography.pxToRem(14)
      : theme.typography.subtitle1.fontSize,
    lineHeight: 1.2,
    fontVariantNumeric: "tabular-nums",
    color: compact ? CARD_STEPPER_INK : theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }
}

/**
 * Informational ADDED pill — same opaque floating surface as the card stepper.
 */
export function getMenuQuantityAddedBadgeSx(theme: Theme): SxProps<Theme> {
  return {
    ...getMenuQuantityStepperShellSx(theme, "compact"),
    height: 34,
    minHeight: 34,
    px: 0.75,
    py: 0,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    lineHeight: 1.1,
    color: CARD_STEPPER_INK,
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
    "&:hover": {
      backgroundColor: CARD_STEPPER_BG,
      boxShadow: CARD_STEPPER_SHADOW,
      border: `1px solid ${CARD_STEPPER_BORDER}`,
    },
  }
}
